import React, { useRef, useState, useEffect, memo, useCallback, useContext, useLayoutEffect } from 'react';
import './Copilot.scss';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactSVG } from 'react-svg';
import sendIcon from '../../Assets/icons/send.svg';
import owl from '../../Assets/icons/owl.png';
import { useNavigate } from 'react-router-dom';
import ApiContext from '../../Api/ApiContext';
import { Paths } from '../../Core/EPath';
import { useEventSubscriber } from '../../Api/ApiEvents/ApiEventSubscriber';
import RestrictReadOnly from '../../Helpers/RestrictReadOnly';
import DOMPurify from "dompurify";

interface CopilotProps {
    documentId: string;
    prompt: string;
    setCopilotPrompt: React.Dispatch<React.SetStateAction<string>>;
    display: boolean;
    copyToEditor: (contentToPaste: any) => void;
    t: (key: any) => any;
    reviewing: boolean;
    typeState: string;
}

const Copilot = memo((props: CopilotProps) => {

    const { documentId, prompt, setCopilotPrompt, display, copyToEditor, t, reviewing, typeState } = props;

    const { user } = useAuth0();

    const [ history, setHistory ] = useState([]);

    const [ fetchingData, setFetchingData ] = useState(true);

    const historyRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef(null);

    const api = useContext(ApiContext);

    const isReseller = process.env.REACT_APP_IS_RESELLER;

    useEffect(() => {
        textareaRef?.current?.focus();
    }, [textareaRef]);

    const navigate = useNavigate();

    useEffect(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight - 20}px`;
        }
    }, []);

    function scrollToBottom() {
        if(historyRef.current) {
            historyRef.current.scrollTop = historyRef.current.scrollHeight;
        }
    }

    useLayoutEffect(() => {
        scrollToBottom();
    });

    const getHistory = useCallback(async () => {
        try {
            const response = await api.post(
                `/chat-history`,
                { documentId },
            );
    
            const historyData = response.data;
            setHistory(historyData);
            setFetchingData(false);
            
            return historyData;
        } catch (error) {
            console.error("Error fetching history:", error);
            return []; 
        }
    }, [documentId, api]);

    const chat = useCallback(async(q) => {
        let mostRecentHistory = history ?? [];
        mostRecentHistory = [...mostRecentHistory, { question: q, answer: "", status: "PROGRESS", type: "REVIEW" }];


        setStreaming(true);

        let lastThreeHistory = mostRecentHistory.slice(-3);
        api.post('/chat',
            { documentId, question: q, history: JSON.stringify(lastThreeHistory) }
        ).then(async(res) => {
            const { messageId } = res.data;

            setHistory((prev) => {
                const newHistory = [...mostRecentHistory];
                newHistory[newHistory.length - 1].id = messageId;
                return newHistory;
            });
        });
    }, [history, documentId, getHistory, api]);

    useEffect(() => {
        if(prompt !== "") {
            chat(prompt);
            setCopilotPrompt("");
        }
    }, [prompt, setCopilotPrompt, chat]);

    useEffect(() => {

        if (!documentId) return;

        api.post('/chat-history',
            { documentId }
        ).then(res => {
            setHistory(res.data);
            setFetchingData(false);
        });

    }, [documentId, api]);

    const [ streaming, setStreaming ] = useState(false);

    useEffect(() => {
        scrollToBottom(); 
    }, [history]);

    useEffect(() => {
        setTimeout(() => scrollToBottom(), 100);    
    }, [display]);

    function pickSuggestion(suggestion) {
        chat(suggestion);
    }

    const Suggestions = () => {

        const suggestions = [
            {
                buttonText: `📃 ${t('copilot.overview_button')}`,
                promptText: t('copilot.overview_prompt')
            },
            {
                buttonText: `💰 ${t('copilot.opportunities_button')}`,
                promptText: t('copilot.opportunities_prompt')
            },
            {
                buttonText: `🚀 ${t('copilot.improvements_button')}`,
                promptText: t('copilot.improvements_prompt')
            },
        ];

        return <div className="prompt-suggestions">
            {suggestions.map((suggestion, index) => {
                    return <div key={index} className="suggestion" onClick={() => pickSuggestion(suggestion.promptText)}>{suggestion.buttonText}</div>
                })
            }
        </div>

    }

    const InitialMessage = () => {

        return (
            <div className="conversation">
            <div className="answer">
                <div className="copilotAvatar">
                    <img src={owl} alt="Copilot" />
                </div>
                <div className="questionBubble">
                    {t("copilot.hey")} 👋<br/>
                    {t("copilot.intro_1")} 👔 💼
                    <br/><br/>
                    {t("copilot.intro_2")}
                    <br/><br/>
                    {t("copilot.intro_3")}
                    <RestrictReadOnly>
                        <Suggestions />
                    </RestrictReadOnly>
                </div>
            </div>
        </div>
        )
    }

    const copyToClipboard = useCallback((text, button) => {
        navigator.clipboard.writeText(text).then(function() {
            button.classList.add("copied");
            button.innerHTML = t('analysis.copiedClipboard');

            setTimeout(() => {
                button.classList.remove("copied");
                button.innerHTML = t('analysis.copyClipboard');
            }, 2000);
        }, function(err) {
            console.error('Could not copy text: ', err);
        });
    }, [t]);

    const copyToContract = useCallback((text, button) => {
        copyToEditor(text);
        button.classList.add("copied");
        button.innerHTML = t('analysis.copiedDocument');

        setTimeout(() => {
            button.classList.remove("copied");
            button.innerHTML = t('analysis.copyDocument');
        }, 2000);
    }, [copyToEditor, t]);

    const addCopy = useCallback(() => {
        const nodes = historyRef.current.querySelectorAll('.draft');
        nodes.forEach(node => {

            if (node.querySelector('button')) {
                return;
            }

            const content = document.createElement('span');
            content.innerHTML = node.innerHTML;
            node.innerHTML = '';
            node.appendChild(content);

            const btn = document.createElement('button');
            btn.innerHTML = t('analysis.copyClipboard');
            btn.setAttribute('data-click-target', 'copy-to-clipboard');
            btn.setAttribute('data-click-source', 'review-copilot');
            btn.onclick = () => copyToClipboard(content.innerText, btn);
            node.appendChild(btn);

            if(isReseller) return;

            const btn2 = document.createElement('button');
            btn2.innerHTML = t('analysis.copyDocument');
            btn2.setAttribute('data-click-target', 'copy-to-document');
            btn2.setAttribute('data-click-source', 'review-copilot');
            btn2.onclick = () => copyToContract(`<div class="copied">${content.innerHTML}</div>`, btn2);
            node.appendChild(btn2);
        });
    }, [t, copyToClipboard, copyToContract]);

    const Answer = (props) => {

        const { answer } = props;

        const [ localAnswer, setLocalAnswer ] = useState(answer);

        useEffect(() => {
            setLocalAnswer(answer);
        }, [answer]);
    
        const unescapeHTML = (html) => {
            const txt = document.createElement('textarea');
            txt.innerHTML = html;
            return txt.value;
        }

        const [content, setContent] = useState(
            localAnswer?.answer === "" ? 
            t("fly.wait") : 
            unescapeHTML(localAnswer?.answer)
        );

        useEffect(() => {
            setContent(
                localAnswer?.answer === "" ? 
                t("fly.wait") : 
                unescapeHTML(localAnswer?.answer)
            );
        }, [localAnswer]);

        useEventSubscriber("stream", answer?.id, (data: any) => {
            setLocalAnswer(data);
            scrollToBottom();
            setStreaming(true);

            if(data?.status === "COMPLETE") {
                getHistory().then((history) => {
                    setHistory(history);
                    addCopy();
                    setStreaming(false);
                });
            }
        });

        useEffect(() => {
            addCopy();
        }, [addCopy]);

        return (
            <div className={"answer"}>
                <div className="copilotAvatar">
                <img src={owl} alt="Copilot" />
                </div>
                <div className="questionBubble">  
                    <div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(content)}} />
                </div>
            </div>
        )
    }

    const History = memo((props: any) => {

        const { history } = props;

        return (
            <div ref={historyRef} className="history">
                <InitialMessage />
                {history.map((element, index) => (
                    <div key={index} className="conversation">
                        <div className="question">
                            <span className="questionBubble"><div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(element.question)}} /></span>
                            <img src={user.picture} alt={user.name} />
                        </div> 
                        <Answer answer={element} />
                    </div>
                ))}
            </div>
        )
    });

    const Form = memo((props: any) => {

        const { streaming, textareaRef } = props;

        const [localQuestion, setLocalQuestion] = useState("");

        useEffect(() => {
            textareaRef?.current?.focus();
        }, [streaming, textareaRef]);

        const handleInputChange = (event) => {
            setLocalQuestion(event.target.value);
        }
    
        const handleSubmit = (event) => {
            event.preventDefault();
            if(localQuestion.trim() !== '') {
                chat(localQuestion);
                setLocalQuestion('');
            }
        }
    
        const handleKeyPress = (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                handleSubmit(event);
            }
        }

        return (
            <form onSubmit={handleSubmit}>
                <textarea
                    value={localQuestion} 
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder={t("copilot.placeholder")}
                    ref={textareaRef}
                    disabled={streaming}
                />
                <button type="submit" disabled={streaming}>
                    <ReactSVG src={sendIcon} />
                </button>
            </form>
        )
    });

    if(fetchingData) return null;

    const unsupportedType = process.env.REACT_APP_IS_RESELLER && typeState === "Unknown";
    const reviewNotStarted = process.env.REACT_APP_IS_RESELLER && !reviewing;

    if(unsupportedType) {
        return <div className={"suggestions " + (display ? "show" : "hide")}>
            <div className="nds settings">
                <p>{t('analysis.unsupported_type')}</p>
                <p><a href="mailto:legal@vdl.be">legal@vdl.be</a></p>
          </div>
        </div>
      }

    if(reviewNotStarted) {
        return <div className={"suggestions " + (display ? "show" : "hide")}>
            <div className="nds settings">
                <p>{t('analysis.review_not_started')}</p>
        </div>
      </div>
    }

    return (
        <div className={"co-pilot " + (display ? "show" : "hide")}>
            <History history={history} />
            <RestrictReadOnly><Form streaming={streaming} textareaRef={textareaRef} /></RestrictReadOnly>
        </div>
    )
});

export default Copilot;
